import { supabase } from '../lib/supabase';
export class DeviceService {
    /**
     * Get all devices with assignment details (admin only)
     */
    static async getAllDevices() {
        const { data, error } = await supabase.rpc('get_all_devices');
        if (error) {
            console.error('Error fetching devices:', error);
            throw new Error(error.message);
        }
        return data || [];
    }
    /**
     * Get all devices assigned to patients in a study
     */
    static async getStudyDevices(studyId) {
        const { data, error } = await supabase.rpc('get_study_devices', {
            p_study_id: studyId
        });
        if (error) {
            console.error('Error fetching study devices:', error);
            throw new Error(error.message);
        }
        return data || [];
    }
    /**
     * Get devices for a specific clinician
     */
    static async getClinicianDevices(clinicianId) {
        const { data, error } = await supabase.rpc('get_clinician_devices', {
            p_clinician_id: clinicianId
        });
        if (error) {
            console.error('Error fetching clinician devices:', error);
            throw new Error(error.message);
        }
        return data || [];
    }
    /**
     * Get a single device by ID
     */
    static async getDeviceById(deviceId) {
        const { data, error } = await supabase
            .from('devices')
            .select('*')
            .eq('id', deviceId)
            .single();
        if (error) {
            console.error('Error fetching device:', error);
            if (error.code === 'PGRST116') {
                return null; // Not found
            }
            throw new Error(error.message);
        }
        return data;
    }
    /**
     * Get a single device by serial number
     */
    static async getDeviceBySerialNumber(serialNumber) {
        const { data, error } = await supabase
            .from('devices')
            .select('*')
            .eq('serial_number', serialNumber)
            .single();
        if (error) {
            if (error.code === 'PGRST116') {
                return null; // Not found
            }
            console.error('Error fetching device by serial:', error);
            throw new Error(error.message);
        }
        return data;
    }
    /**
     * Create a new device (admin only - uses direct insert)
     * Optionally assigns the device to a study if studyId is provided
     */
    static async createDevice(serialNumber, isTestDevice = false, studyId) {
        const { data, error } = await supabase
            .from('devices')
            .insert({
            serial_number: serialNumber,
            is_test_device: isTestDevice
        })
            .select()
            .single();
        if (error) {
            console.error('Error creating device:', error);
            if (error.code === '23505') {
                throw new Error('A device with this serial number already exists');
            }
            throw new Error(error.message || 'Failed to create device');
        }
        // Assign to study if studyId is provided
        if (studyId) {
            await this.assignDeviceToStudy(data.id, studyId);
        }
        return data;
    }
    /**
     * Register a device by serial number (any authenticated user)
     * Uses a security definer function to bypass RLS
     * Returns existing device if already registered, or creates new one
     */
    static async registerDevice(serialNumber) {
        const { data, error } = await supabase.rpc('register_device', {
            p_serial_number: serialNumber
        });
        if (error) {
            console.error('Error registering device:', error);
            throw new Error(error.message || 'Failed to register device');
        }
        return data;
    }
    /**
     * Assign device to study (admin only)
     */
    static async assignDeviceToStudy(deviceId, studyId) {
        const { error } = await supabase.rpc('assign_device_to_study', {
            p_device_id: deviceId,
            p_study_id: studyId
        });
        if (error) {
            console.error('Error assigning device to study:', error);
            throw new Error(error.message || 'Failed to assign device to study');
        }
    }
    /**
     * Remove device from study (admin only)
     */
    static async removeDeviceFromStudy(deviceId) {
        const { error } = await supabase.rpc('remove_device_from_study', {
            p_device_id: deviceId
        });
        if (error) {
            console.error('Error removing device from study:', error);
            throw new Error(error.message || 'Failed to remove device from study');
        }
    }
    /**
     * Get all users who have used a specific device
     */
    static async getDeviceUsers(deviceId) {
        const { data, error } = await supabase.rpc('get_device_users', {
            p_device_id: deviceId
        });
        if (error) {
            console.error('Error fetching device users:', error);
            throw new Error(error.message || 'Failed to fetch device users');
        }
        return data || [];
    }
    /**
     * Update device details (admin only)
     */
    static async updateDevice(deviceId, serialNumber) {
        const { data, error } = await supabase
            .from('devices')
            .update({
            serial_number: serialNumber,
            updated_at: new Date().toISOString()
        })
            .eq('id', deviceId)
            .select()
            .single();
        if (error) {
            console.error('Error updating device:', error);
            if (error.code === '23505') {
                throw new Error('A device with this serial number already exists');
            }
            throw new Error(error.message || 'Failed to update device');
        }
        return data;
    }
    /**
     * Delete a device (admin only)
     */
    static async deleteDevice(deviceId) {
        const { error } = await supabase.from('devices').delete().eq('id', deviceId);
        if (error) {
            console.error('Error deleting device:', error);
            throw new Error(error.message || 'Failed to delete device');
        }
    }
    /**
     * Set the test device flag (admin only)
     */
    static async setTestDeviceFlag(deviceId, isTestDevice) {
        const { data, error } = await supabase.rpc('set_device_test_flag', {
            p_device_id: deviceId,
            p_is_test: isTestDevice
        });
        if (error) {
            console.error('Error setting test device flag:', error);
            throw new Error(error.message || 'Failed to set test device flag');
        }
        return data;
    }
    /**
     * Update firmware version on a device record
     */
    static async updateFirmwareVersion(deviceId, firmwareVersion) {
        const { error } = await supabase.rpc('update_device_firmware_version', {
            p_device_id: deviceId,
            p_firmware_version: firmwareVersion
        });
        if (error) {
            console.error('Error updating firmware version:', error);
            throw new Error(error.message);
        }
    }
    /**
     * Get all devices marked as test devices
     */
    static async getTestDevices() {
        const { data, error } = await supabase.rpc('get_test_devices');
        if (error) {
            console.error('Error fetching test devices:', error);
            throw new Error(error.message || 'Failed to fetch test devices');
        }
        return data || [];
    }
}
